import { memo, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelToolGroupLoader } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/WorkspaceAgentPanelToolGroupLoader';
import { WorkspaceAgentPanelWaitingForUserLoader } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/WorkspaceAgentPanelWaitingForUserLoader';
import { WorkspaceAgentDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/disclosure/WorkspaceAgentDisclosure';
import { type WorkspaceAgentPhaseStatus } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/types';
import { WorkspaceAgentPhaseList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/WorkspaceAgentPhaseList';
import {
  type WorkspaceAgentRunOutcome,
  type WorkspaceAgentRunStatus,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { formatWorkspaceAgentRunSettledLabel } from '~/components/v2/components/workspace/workspace-agent-panel/utils/formatWorkspaceAgentRunSettledLabel';
import {
  getWorkspaceAgentActiveRunLabel,
  WORKSPACE_AGENT_CANCELLING_RUN_LABEL,
  WORKSPACE_AGENT_CANCELLING_RUN_LABEL_FULL,
  WORKSPACE_AGENT_DEFAULT_ACTIVE_RUN_LABEL,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentActiveRunLabel';
import {
  type WorkspaceAgentDisplayTool,
  type WorkspaceAgentToolStatus,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/groupWorkspaceAgentMessagesForDisplay';
import {
  isWorkspaceAgentChoiceToolName,
  isWorkspaceAgentElicitationFollowUpHold,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentElicitationFollowUpHold';

const isChoiceToolInProgress = (tool: WorkspaceAgentDisplayTool): boolean =>
  isWorkspaceAgentChoiceToolName(tool.name) && (tool.status === 'working' || tool.status === 'awaitingApproval');

const PHASE_STATUS_BY_TOOL_STATUS: Record<WorkspaceAgentToolStatus, WorkspaceAgentPhaseStatus> = {
  working: 'running',
  completed: 'done',
  error: 'error',
  awaitingApproval: 'awaitingApproval',
  cancelled: 'cancelled',
};

export type WorkspaceAgentPanelRunGroupProps = {
  finishedAt?: string;
  /** User answered; keep a loader up until the server's next activity replaces it. */
  isAwaitingElicitationFollowUp: boolean;
  outcome?: WorkspaceAgentRunOutcome;
  startedAt?: string;
  status: WorkspaceAgentRunStatus;
  tools: WorkspaceAgentDisplayTool[];
};

export const WorkspaceAgentPanelRunGroup = memo(
  ({
    finishedAt,
    isAwaitingElicitationFollowUp,
    outcome,
    startedAt,
    status,
    tools,
  }: WorkspaceAgentPanelRunGroupProps) => {
    const visibleTools = useMemo(() => tools.filter((tool) => !isWorkspaceAgentChoiceToolName(tool.name)), [tools]);
    const phases = useMemo(
      () =>
        visibleTools.map((tool) => ({
          id: tool.id,
          label: tool.label,
          status: PHASE_STATUS_BY_TOOL_STATUS[tool.status],
        })),
      [visibleTools],
    );

    const failedStepCount = useMemo(
      () => visibleTools.filter((tool) => tool.status === 'error').length,
      [visibleTools],
    );

    const settledLabel = useMemo(
      () =>
        formatWorkspaceAgentRunSettledLabel({
          failedStepCount,
          finishedAt,
          outcome,
          startedAt,
        }),
      [failedStepCount, finishedAt, outcome, startedAt],
    );

    const activeRunLabel = useMemo(() => getWorkspaceAgentActiveRunLabel({ tools: visibleTools }), [visibleTools]);
    const isWaitingForUser =
      status === 'active' && !isAwaitingElicitationFollowUp && tools.some(isChoiceToolInProgress);
    const isHoldingForElicitationFollowUp = isWorkspaceAgentElicitationFollowUpHold({
      isAwaitingElicitationFollowUp,
      tools,
    });

    const content = useMemo(() => {
      if (isHoldingForElicitationFollowUp) {
        return (
          <WorkspaceAgentPanelToolGroupLoader
            animate
            className="mt-0.5 size-6 shrink-0"
            data-testid="WORKSPACE_AGENT_TOOL_CALL_GROUP_LOADER"
            text={WORKSPACE_AGENT_DEFAULT_ACTIVE_RUN_LABEL}
          />
        );
      }

      if (isWaitingForUser) {
        return <WorkspaceAgentPanelWaitingForUserLoader />;
      }

      if (status === 'active') {
        return (
          <>
            <WorkspaceAgentPanelToolGroupLoader
              animate
              className="mt-0.5 size-6 shrink-0"
              data-testid="WORKSPACE_AGENT_TOOL_CALL_GROUP_LOADER"
              text={activeRunLabel}
            />
            {phases.length > 0 ? (
              <div className="pl-8">
                <WorkspaceAgentPhaseList itemTestId="WORKSPACE_AGENT_TOOL_CALL" phases={phases} variant="steps" />
              </div>
            ) : null}
          </>
        );
      }

      if (status === 'cancelling') {
        return (
          <>
            <output className="sr-only">{WORKSPACE_AGENT_CANCELLING_RUN_LABEL_FULL}</output>
            <div className="flex min-w-0 items-center gap-2">
              <WorkspaceAgentPanelToolGroupLoader
                animate
                className="mt-0.5 size-6 shrink-0"
                data-testid="WORKSPACE_AGENT_TOOL_CALL_GROUP_LOADER"
              />
              <span
                className="min-w-0 truncate font-mono ads:text-metadata text-12 text-gray-10 leading-4"
                data-testid="WORKSPACE_AGENT_TOOL_CALL_GROUP_LOADER_TEXT"
                title={WORKSPACE_AGENT_CANCELLING_RUN_LABEL_FULL}
              >
                {WORKSPACE_AGENT_CANCELLING_RUN_LABEL}
              </span>
            </div>
            {phases.length > 0 ? (
              <div className="pl-8">
                <WorkspaceAgentPhaseList itemTestId="WORKSPACE_AGENT_TOOL_CALL" phases={phases} variant="steps" />
              </div>
            ) : null}
          </>
        );
      }

      if (phases.length === 0) {
        return <WorkspaceAgentPanelToolGroupLoader animate={false} text={settledLabel} />;
      }

      return (
        <WorkspaceAgentDisclosure
          data-testid="WORKSPACE_AGENT_RUN_GROUP_DISCLOSURE"
          defaultIsExpanded={false}
          trigger={
            <span className="block truncate font-mono font-semibold ads:text-metadata text-10 text-gray-10 leading-5">
              {settledLabel}
            </span>
          }
          triggerTooltip={settledLabel}
          variant="plain"
        >
          <WorkspaceAgentPhaseList phases={phases} variant="panel" />
        </WorkspaceAgentDisclosure>
      );
    }, [activeRunLabel, isHoldingForElicitationFollowUp, isWaitingForUser, phases, settledLabel, status]);

    if (
      status === 'finished' &&
      outcome?.type !== 'error' &&
      outcome?.type !== 'cancelled' &&
      !isHoldingForElicitationFollowUp &&
      tools.length > 0 &&
      visibleTools.length === 0
    ) {
      return null;
    }

    return (
      <div
        className={cn(
          'min-w-0 self-start',
          (status === 'active' || status === 'cancelling') && 'flex w-full flex-col gap-1 overflow-hidden',
        )}
        data-testid="WORKSPACE_AGENT_RUN_GROUP"
      >
        {content}
      </div>
    );
  },
);

WorkspaceAgentPanelRunGroup.displayName = 'WorkspaceAgentPanelRunGroup';
